/*
 * -----------------------------------------------------------------------
 *  <copyright file="screen-permissions-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { Checkbox } from '@progress/kendo-react-inputs';
import { useTranslation, useGridCells } from 'hooks';
import { IPermission } from 'interfaces/permission.interface';
import { OrganisationRoleFields_OrganisationRole_rights_OrganisationRoleRight } from 'queries/generated/fragments.generated';
import ExpandableGrid from 'views/components/core/expandable/grid';

import './styles.scss';

const ScreenPermissionsGrid = ({ role, screenPermissions, updatePermissions, isEditable }: any) => {
    const { t } = useTranslation();
    const { CustomizableCell } = useGridCells();

    const [permissions, setPermissions] = useState([]);

    useEffect(() => {
        if (role?.rights?.length && screenPermissions?.length) {
            const array = screenPermissions.map((item: any) => {
                const element = role.rights?.find(
                    (el: OrganisationRoleFields_OrganisationRole_rights_OrganisationRoleRight) =>
                        el.element === item.element,
                );
                return {
                    ...item,
                    permissions: element?.permissions ?? item.possiblePermissions,
                };
            });
            setPermissions(array);
        } else if (screenPermissions?.length) {
            const array = screenPermissions.map((item: any) => ({
                ...item,
            }));
            setPermissions(array);
        }
    }, [role]);

    const handlePermissions = (item: IPermission, value: number) => {
        const array = permissions;
        const index = array.findIndex((el: IPermission) => el.element === item.element);
        const element = array[index];
        element.permissions = value ? 1 : 0;
        setPermissions(array);
        updatePermissions(array);
    };

    return (
        <>
            {!role && <p className="no-role-selected">{t('uam:selectRoleToCustomise')}</p>}
            {role && (
                <ExpandableGrid
                    customKey="data-permissions-grid"
                    items={permissions}
                    showPager
                    groupable={false}
                    scrollable="scrollable"
                    reorderable
                    pageable={false}
                    resizable
                    showFirstColumn={false}
                    addScroll
                    options={{
                        allowExcelExport: false,
                        allowExpandCollapseAll: false,
                        useFilterChips: false,
                    }}
                    detail={null}
                    columns={[
                        {
                            field: 'moduleName',
                            id: 'module-name',
                            label: t('module'),
                            minWidth: 230,
                            show: true,
                        },
                        {
                            field: 'title',
                            id: 'data-screen',
                            label: t('screen'),
                            minWidth: 230,
                            show: true,
                        },
                        {
                            field: 'active',
                            id: 'data-display',
                            label: t('display'),
                            minWidth: 230,
                            show: true,
                            cells: {
                                data: CustomizableCell((dataItem: any) => (
                                    <Checkbox
                                        rounded={null}
                                        disabled={!isEditable}
                                        checked={dataItem.permissions}
                                        onChange={(event: any) => {
                                            handlePermissions(dataItem, event.value);
                                        }}
                                        data-testid="display-checkbox"
                                        size="medium"
                                    />
                                )),
                            },
                        },
                    ]}
                />
            )}
        </>
    );
};

export default ScreenPermissionsGrid;
